import { useState, useEffect, useRef } from "react";
import { sanitizeHtml } from '../../util/sanitizeHtml';
import { useAskMentohaMutation } from "../../api-integration/secure/secure";
import { FaTelegram } from 'react-icons/fa'; 
import Mentoha from '../../assets/images/mentoha.png';
import "../../assets/styles/mentohaChatBot.scss";
import { convertMarkdownToHtml, getTranslatedText } from "../../util/util";
import usePageTranslation from "../../hooks/ui/usePageTranslation";


const ChatBot = () => {
    const PageTranslation = usePageTranslation();
    const [chatMessages, setChatMessages] = useState([
        { sender: "bot", content: "Hi, how may I help you?", timestamp: new Date() }
    ]);
    const [isChatVisible, setIsChatVisible] = useState(false);
    const [userInput, setUserInput] = useState("");

    const chatBottomRef = useRef<HTMLDivElement | null>(null);

    const [triggerAskMentoha, { data: mentohaResponse, isLoading: isMentohaLoading, isSuccess: isMentohaSuccess, isError: isMentohaError, error: mentohaError}] 
    = useAskMentohaMutation();

    useEffect(() => {
        scrollToLatestMessage();
    }, [chatMessages, isMentohaLoading]);

    useEffect(() => {
        if (isMentohaSuccess) {
            if(mentohaResponse?.success === true) {
                const currentTime = new Date(); 
                setChatMessages(prev => [
                    ...prev,
                    {
                    sender: "bot",
                    content: convertMarkdownToHtml(mentohaResponse?.AIADVICE, 'stdText') || "",
                    timestamp: currentTime
                    }
                ]);
            }
            else {
                setChatMessages(prev => [...prev, { sender: "bot", content: "Sorry, I couldn't process your request. Please try again.", timestamp: new Date() }]);
            }
        }
        else if(isMentohaError || mentohaError) {
            const currentTime = new Date(); 
            setChatMessages(prev => [
                ...prev,
                {
                    sender: "bot",
                    content: mentohaResponse?.message || "An error occurred while processing your request.",
                    timestamp: currentTime
                }
            ]);
        }
    }, [isMentohaSuccess, isMentohaError, mentohaError]);

    const scrollToLatestMessage = () => {
        chatBottomRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleSendMessage = () => {
        if (!userInput.trim()) return;
        const currentTime = new Date();

         setChatMessages(prev => [
            ...prev,
            {
            sender: "user",
            content: userInput,
            timestamp: currentTime
            }
        ]);
        triggerAskMentoha({ userPrompt: userInput });
        setUserInput("");
    };

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter") handleSendMessage();
    };

    return (
        <>
        {/* Chat Toggle Button */}
        {isChatVisible === false && (
            <button
                onClick={() => setIsChatVisible(!isChatVisible)}
                className="btn btn-light rounded-circle shadow position-fixed bottom-0 end-0 m-4"
                style={{ width: "3.75rem", height: "3.75rem", zIndex: 1000 }}
            >
                <img src={Mentoha} alt={getTranslatedText('imageAlt.text',PageTranslation)} className="img-fluid h-2-2" />
            </button>
        )}

        {/* Chat Window */}
       {isChatVisible && (
            <div
                className="border bg-white rounded shadow position-fixed bottom-0 end-0 me-4 mb-5 chat-window-container"
                style={{ 
                    width: "min(90vw, 31.25rem)", 
                    maxWidth: "31.25rem", 
                    maxHeight: "75vh",
                    zIndex: 999 
                }}
            >
                {/* AI Face Image */}
                <div className="aiAgent-face start-50 translate-middle-x">
                <div className="halfAiAgentFace">
                    <img
                    src={Mentoha}
                    alt={getTranslatedText('imageAlt.text',PageTranslation)}
                    />
                </div>
                </div>

                {/* Header */}
                <div className="bg-white rounded-top pt-5 pb-3 d-flex flex-column align-items-center position-relative">
                    <div className="fs-5 fw-bold text-center mt-1">Mentoha AI Assistant</div>
                    <button
                        type="button"
                        className="btn-close position-absolute top-0 end-0 m-3"
                        aria-label="Close"
                        onClick={() => {
                            setIsChatVisible(false);
                            setChatMessages([
                                { sender: "bot", content: "Hi, how may I help you?", timestamp: new Date() }
                            ]);
                        }}
                    />
                </div>
                <hr />

                {/* Chat Messages */}
                <div
                    className="px-3 py-2 border-bottom d-flex flex-column overflow-auto chat-messages-container"
                    style={{ 
                        height: "min(60vh, 400px)", 
                        minHeight: "250px",
                        maxHeight: "500px",
                        scrollBehavior: "smooth" 
                    }}
                >
                    {chatMessages.map((message, index) => (
                    <div
                        key={index}
                        className={`d-flex flex-column mb-3 ${message.sender === "user" ? "align-items-end" : "align-items-start"}`}
                    >
                        <div ref={index === chatMessages.length - 1 ? chatBottomRef : null} className="d-flex align-items-start" style={{ maxWidth: "80%" }}>
                        
                        {/* Bot Icon */}
                        {message.sender === "bot" && (
                            <div className="me-2 mt-1 flex-shrink-0">
                            <img
                                src={Mentoha}
                                alt={getTranslatedText('imageAlt.text', PageTranslation)}
                                className="img-fluid h-1-5"
                            />
                            </div>
                        )}
                        <div>
                            {/* Message Bubble */}
                            <div
                                className={`px-3 py-2 shadow-sm chat-bubble ${
                                    message.sender === "user" ? "user-bubble" : "bot-bubble"
                                }`}
                                style={{
                                    backgroundColor: message.sender === "user" ? "#d1ecf1" : "lightgray",
                                    color: "inherit",
                                    fontWeight: "inherit",
                                    fontFamily: "inherit",
                                    fontSize: "inherit",
                                    whiteSpace: "normal",
                                    wordWrap: "break-word",
                                }}
                            >
                                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }} />
                            </div>
                            {/* Timestamp aligned to right of message bubble */}
                            <div
                                className="text-muted small mt-1 text-end"
                                style={{ fontSize: "0.75rem" }}
                            >
                                {message.timestamp &&
                                    new Date(message.timestamp).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit"
                                })}
                            </div>
                        </div>
                        </div>
                    </div>
                ))}
                {isMentohaLoading && (
                    <div className="d-flex flex-column mb-3 align-items-start">
                        <div className="d-flex align-items-start" style={{ maxWidth: "80%" }}> 
                        {/* Bot Icon */}
                        <div className="me-2 mt-1 flex-shrink-0">
                            <img
                            src={Mentoha}
                            alt={getTranslatedText('imageAlt.text', PageTranslation)}
                            className="img-fluid h-1-5"
                            />
                        </div>
                        {/* Message Bubble with Loader */}
                        <div>
                            <div
                            className="px-3 py-2 shadow-sm chat-bubble bot-bubble"
                            style={{
                                backgroundColor: "lightgray",
                                fontSize: "inherit",
                                fontFamily: "inherit",
                                fontWeight: "inherit",
                                color: "inherit",
                            }}
                            >
                            <div className="ball-loader">
                                <div></div>
                                <div></div>
                                <div></div>
                            </div>
                            </div>
                        </div>
                        </div>
                    </div>
                )}
                <div ref={chatBottomRef}></div>
                </div>
                <div className="mb-2 mt-1 modal-footer fixed-bottom position-relative w-100 px-4">
                    <div className="position-relative w-100">
                        <input 
                            type="text" 
                            className="form-control oval-input pe-5"  // added 'pe-5' to give right padding for button
                            placeholder="Type your message..."
                            value={userInput || ""} 
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button 
                            type="submit" 
                            className="btn btn-rounded-pill p-1 send-button" 
                            onClick={handleSendMessage}
                        >
                            <FaTelegram color="#0077B5" size="1rem"/>
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default ChatBot;
